import { Fragment, memo, Suspense, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { leftSideBarCasinoMain } from 'utils/casino/casino';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import { CUSTOM_PROVIDERS } from 'utils/constants/casino/custom-providers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { NewCasinoGame } from 'interfaces/new-casino';
import { EmptyCasinoGamesResult } from './__partials/components/EmptyCasinoGamesResult';
import { GamesByCategory } from './__partials/components/GamesByCategory';
import { CasinoGamesByCategorySkeleton } from './__partials/components/GamesByCategory/Skeleton';
import { useGamesCombinedData } from './__partials/hooks/useGamesCombinedData';
import { useGamesOrder } from './__partials/hooks/useGamesOrder';
import { useGamesQuery } from './__partials/hooks/useGamesQuery';
import { useSelectedCategory } from './__partials/hooks/useSelectedCategory';
import { useCasinoGamesConditionalsValues } from './__partials/providers/CasinoGamesConditionalsProvider';
import { useCasinoGamesConfigsValues } from './__partials/providers/CasinoGamesConfigsContext';
import { useCasinoGamesFetchDisabledValues } from './__partials/providers/CasinoGamesFetchDisabledContext';
import { useCasinoGamesFetchParamsValues } from './__partials/providers/CasinoGamesFetchParamsContext';
import { CasinoGamesConditionalsActionType } from './__partials/state/conditionals-reducer';
import { LazyLoad } from 'components/Shared/LazyLoad';
import { setGamesConfigs } from 'store/actions/new-casino';
import { getFavoriteProviders } from 'store/selectors/fav-data';
import { useCategories } from '../Categories/__partials/hooks/useCategories';
import './index.less';

const { MockInitializer } = lazy(
  () =>
    import(
      /* webpackChunkName: "CasinoGamesMockInitializer" */ './__partials/components/MockInitializer'
    )
);

const { FavoriteProviders } = lazy(
  () =>
    import(
      /* webpackChunkName: "my-casino-providers" */ './__partials/components/FavoriteProviders'
    )
);

const LOAD_COUNT_DESKTOP = 5;
const LAZY_LOAD_INITIAL_COUNT_MOBILE = 3;
const LAZY_LOAD_INITIAL_COUNT = 5;

const _CasinoGamesContent = () => {
  const { configs } = useCasinoGamesConfigsValues();
  const { fetchParams } = useCasinoGamesFetchParamsValues();
  const { isFetchDisabled } = useCasinoGamesFetchDisabledValues();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const { selectedCategory } = useSelectedCategory({ configs });

  const categories = useCategories(leftSideBarCasinoMain());
  const isFavoriteCategory = selectedCategory === CUSTOM_CATEGORIES.favorite.id;
  const { conditionals, dispatchConditionals } =
    useCasinoGamesConditionalsValues();

  const favoriteProviders = useSelector(getFavoriteProviders);
  const {
    data,
    paramsWithoutPaginationStringified,
    isLoading: isQueryLoading
  } = useGamesQuery({
    configs: configs,
    params: fetchParams,
    isFetchDisabled: isFetchDisabled
  });

  const { combinedData, setCombinedData } = useGamesCombinedData({
    data,
    paramsWithoutPaginationStringified
  });

  // setting loading state for load more functionality
  useEffect(() => {
    dispatchConditionals({
      type: CasinoGamesConditionalsActionType.SET,
      payload: {
        queryLoading: isQueryLoading
      }
    });
  }, [isQueryLoading]);

  useEffect(() => {
    dispatch(setGamesConfigs(configs));
  }, [configs]);

  const unorderedGames = useMemo(() => {
    if (!combinedData.data?.data?.games) {
      return null;
    }

    return Object.entries(combinedData.data.data.games)
      .map(([id, _games]) => {
        const providerIdsConfig = configs.providerIds;
        const filteredGames =
          providerIdsConfig && providerIdsConfig !== CUSTOM_PROVIDERS.all.name
            ? _games.games?.filter((game: NewCasinoGame) =>
                providerIdsConfig.includes(game.provider)
              )
            : _games.games;

        if (!filteredGames?.length) {
          return null;
        }

        return {
          id,
          count: _games.count,
          games: filteredGames,
          categoryName: _games.games?.[0]?.cats?.[0]?.title || ''
        };
      })
      .filter(Boolean);
  }, [combinedData]);

  const { orderedGames: games } = useGamesOrder({ games: unorderedGames });
  const isDisableProviders = configs.favorite?.disableProviders;

  const gamesData = useMemo(() => {
    if (games?.length) {
      const orderMap = new Map(
        categories?.map((value, index) => {
          return [value.id, index];
        })
      );

      const sortedGames = games.sort((a, b) => {
        const indexA = orderMap.has(a?.id || '')
          ? orderMap.get(a?.id || '') || Infinity
          : Infinity;

        const indexB = orderMap.has(b?.id || '')
          ? orderMap.get(b?.id || '') || Infinity
          : Infinity;

        return indexA - indexB;
      });

      return configs.categoriesLength
        ? sortedGames.slice(0, configs.categoriesLength)
        : sortedGames;
    }

    return [];
  }, [games, configs.categoriesLength]);

  const isEmptyResults = useMemo(() => {
    if (conditionals.emptyResults) {
      return conditionals.emptyResults;
    }

    if (
      SpringConfigs.USER_PERSONALIZATION_ENABLED &&
      isFavoriteCategory &&
      !isDisableProviders
    ) {
      if (
        games === undefined ||
        games === null ||
        favoriteProviders === undefined ||
        favoriteProviders === null
      ) {
        return false;
      }

      return !games?.length && !favoriteProviders?.length;
    }

    return games ? games?.length === 0 : false;
  }, [
    conditionals.emptyResults,
    favoriteProviders?.length,
    games,
    isFavoriteCategory,
    isDisableProviders
  ]);

  const isExternalLoading =
    combinedData.data?.data?.games &&
    Object.keys(combinedData.data?.data?.games)[0] === '';

  const isLoading = SpringConfigs.MOCKED_DATA
    ? false
    : conditionals.loading || !combinedData.data || isExternalLoading;

  const favProvidersEnabledSetting =
    isFavoriteCategory &&
    SpringConfigs.USER_PERSONALIZATION_ENABLED &&
    !configs.favorite?.disableProviders;

  const favProvidersEnabled =
    favoriteProviders?.length && favProvidersEnabledSetting;

  if (isEmptyResults) {
    return <EmptyCasinoGamesResult />;
  }

  if (isLoading) {
    return (
      <CasinoGamesByCategorySkeleton
        favProvidersEnabled={favProvidersEnabledSetting || false}
      />
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {SpringConfigs.MOCKED_DATA && (
        <Suspense>
          <MockInitializer setCombinedData={setCombinedData} />
        </Suspense>
      )}
      {!SpringConfigs.MOCKED_DATA &&
      SpringConfigs.USER_PERSONALIZATION_ENABLED &&
      favProvidersEnabled ? (
        <Suspense
          fallback={
            <CasinoGamesByCategorySkeleton
              favProvidersEnabled={favProvidersEnabled || false}
            />
          }
        >
          <FavoriteProviders showTitle={configs.showCategoryName} />
        </Suspense>
      ) : null}
      <LazyLoad
        initialCount={
          configs.groupAllByCategories
            ? Infinity
            : isMobile()
            ? LAZY_LOAD_INITIAL_COUNT_MOBILE
            : LAZY_LOAD_INITIAL_COUNT
        }
        loadCount={isMobile() ? 1 : LOAD_COUNT_DESKTOP}
        data={gamesData}
        renderer={(categoryGames: {
          id: string;
          count: string;
          games: NewCasinoGame[];
          categoryName: string;
        }) => (
          <Fragment key={categoryGames.id}>
            <GamesByCategory
              category={{
                id: categoryGames.id,
                name: categoryGames.categoryName,
                gamesCount: Number(categoryGames.count)
              }}
              data={categoryGames.games}
              parentRef={containerRef}
            />
          </Fragment>
        )}
      />
    </div>
  );
};

export const CasinoGamesContent = memo(_CasinoGamesContent);
